import AddRoomButton from 'components/Common/AddRoomButton'
import noop from 'lib/function/noop'
import React, { useCallback, useMemo } from 'react'
import { AdultLabel, generateRoomOccupants } from 'lib/offer/occupancyUtils'
import config from 'constants/config'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import OccupancyItemVertical from './OccupancyItemVertical'

interface Props {
  occupancies: Array<App.Occupants | App.RoomOccupants>;
  maxRooms?: number;
  capacities?: Array<App.RoomCapacity>;
  maxChildAge?: number;
  maxInfantAge?: number;
  saleUnit?: string;
  onChange?: (occupancies: Array<App.Occupants | App.RoomOccupants>) => void;
  name?: string;
  'data-testid'?: string;
  className?: string;
  childrenAgeLabel?: string
  enableMultiRoom?: boolean
  adultLabelType?: AdultLabel;
  showChildren?: boolean;
  offerType?: App.OfferType;
}

const OccupancyPickerVertical = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    occupancies,
    capacities,
    maxChildAge,
    maxInfantAge,
    saleUnit = 'room',
    onChange = noop,
    maxRooms,
    name = 'occupants',
    className,
    childrenAgeLabel = 'Age at time of check-in',
    enableMultiRoom,
    adultLabelType,
    showChildren,
    offerType,
  } = props

  const isInstanceOfRoomOccupants = useMemo(() => occupancies.length && ('roomId' in occupancies[0]), [occupancies])

  const onAddAnotherRoom = useCallback(() => {
    const newRoom = isInstanceOfRoomOccupants ? generateRoomOccupants(config.search.defaultOccupants) : { ...config.search.defaultOccupants }
    const newOccupancies = [...occupancies, newRoom]
    onChange(newOccupancies)
  }, [occupancies, onChange, isInstanceOfRoomOccupants])

  const onItemChange = useCallback((index: number, newOccupancy: App.Occupants) => {
    const newOccupancies = [...occupancies]
    newOccupancies[index] = { ...newOccupancies[index], ...newOccupancy }
    onChange(newOccupancies)
  }, [occupancies, onChange])

  return (<VerticalSpacer gap={20} className={className} data-testid={props['data-testid']} ref={ref}>
    {occupancies.map((occupants, index) => <OccupancyItemVertical
      key={index}
      itemId={index}
      occupancy={occupants}
      title={`${saleUnit} ${index + 1}`}
      maxChildAge={maxChildAge}
      maxInfantAge={maxInfantAge}
      onChange={onItemChange}
      capacities={capacities}
      childrenAgeLabel={childrenAgeLabel}
      name={name}
      adultLabelType={adultLabelType}
      showChildren={showChildren}
      offerType={offerType}
    />)}
    {enableMultiRoom && <div>
      <AddRoomButton
        maxRooms={maxRooms}
        saleUnit={saleUnit}
        roomsCount={occupancies.length}
        onClick={onAddAnotherRoom}
      />
    </div>}
  </VerticalSpacer>
  )
})

OccupancyPickerVertical.displayName = 'OccupancyPicker'

export default React.memo(OccupancyPickerVertical)
